import { useState, useEffect } from 'react';

export default function InteractiveBarChart({ data, labels, colors = ["#4f46e5", "#e2e8f0"] }) {
  const [hov, setHov] = useState(null);
  const [hovBar, setHovBar] = useState(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => { const t = setTimeout(() => setMounted(true), 120); return () => clearTimeout(t); }, []);

  if (!Array.isArray(data) || data.length === 0 || !Array.isArray(labels) || labels.length === 0) {
    return (
      <div className="py-16 text-center text-muted dark:text-slate-400 text-sm border border-dashed border-border dark:border-slate-700 rounded-xl bg-slate-50/10 dark:bg-slate-900/30">
        No chart data available yet.
      </div>
    );
  }

  const max = Math.max(...data.flatMap((d) => d));
  const yMax = Math.ceil(max / 50000) * 50000;
  const CHART_H = 120;
  const Y_TICKS = 4;
  const yTicks = Array.from({ length: Y_TICKS + 1 }, (_, i) => Math.round((yMax / Y_TICKS) * i));

  const barH = (v) => Math.round((v / yMax) * CHART_H);
  const fmt = (v) => (v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : v >= 1000 ? `₹${(v / 1000).toFixed(0)}k` : `₹${v}`);

  return (
    <div style={{ userSelect: "none" }}>
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ display: "flex", flexDirection: "column-reverse", justifyContent: "space-between", height: CHART_H, paddingBottom: 0, flexShrink: 0 }}>
          {yTicks.map((v, i) => (
            <span key={i} style={{ fontSize: 9, color: "#64748b", whiteSpace: "nowrap", lineHeight: 1 }} className="text-slate-600 dark:text-slate-400 font-medium">
              {fmt(v)}
            </span>
          ))}
        </div>

        <div style={{ flex: 1, position: "relative" }}>
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column-reverse", justifyContent: "space-between", pointerEvents: "none", height: CHART_H }}>
            {yTicks.map((_, i) => (
              <div key={i} style={{ width: "100%", height: 1, background: "var(--color-border)", opacity: i === 0 ? 0.8 : 0.3 }} />
            ))}
          </div>

          <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: CHART_H, position: "relative", zIndex: 1 }}>
            {data.map((pair, i) => {
              const isHov = hov === i;
              const h0 = barH(pair[0]);
              const h1 = barH(pair[1]);
              return (
                <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", position: "relative", height: "100%", justifyContent: "flex-end" }}>
                  {isHov && (
                    <div style={{ position: "absolute", top: Math.max(0, CHART_H - Math.max(h0, h1) - 84), left: "50%", transform: "translateX(-50%)", background: "rgba(15, 23, 42, 0.98)", color: "#f8fafc", fontSize: 10, padding: "10px 12px", borderRadius: 8, whiteSpace: "nowrap", zIndex: 20, pointerEvents: "none", boxShadow: "0 12px 30px rgba(0,0,0,0.55)", minWidth: 140, border: "1px solid rgba(148,163,184,0.15)" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6, borderBottom: "1px solid rgba(148,163,184,0.16)", paddingBottom: 5 }} className="dark:text-slate-300">
                        {labels[i]}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, marginBottom: 4 }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 5, color: hovBar === "collected" ? "#a5b4fc" : "#cbd5e1" }}>
                          <span style={{ width: 7, height: 7, borderRadius: 2, background: "#6366f1", display: "inline-block" }} />
                          Collected
                        </span>
                        <span style={{ fontWeight: 800, color: hovBar === "collected" ? "#a5b4fc" : "#f8fafc" }}>{fmt(pair[0])}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, marginBottom: 6 }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 5, color: "#cbd5e1" }}>
                          <span style={{ width: 7, height: 7, borderRadius: 2, background: "#94a3b8", display: "inline-block" }} />
                          Target
                        </span>
                        <span style={{ fontWeight: 800, color: hovBar === "target" ? "#cbd5e1" : "#f8fafc" }}>{fmt(pair[1])}</span>
                      </div>
                      <div style={{ borderTop: "1px solid rgba(148,163,184,0.16)", paddingTop: 5 }}>
                        {(() => {
                          const pct = pair[1] > 0 ? Math.round((pair[0] / pair[1]) * 100) : 0;
                          const gap = pair[1] - pair[0];
                          const col = pct >= 100 ? "#34d399" : pct >= 80 ? "#fbbf24" : "#f87171";
                          return (
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9 }}>
                              <span style={{ color: "#94a3b8" }}>{pct >= 100 || (pair[1] === 0 && pair[0] >= 0) ? "✓ Target met" : `Gap: ${fmt(gap)}`}</span>
                              <span style={{ fontWeight: 800, color: col }}>{pair[1] === 0 ? 'N/A' : `${pct}%`}</span>
                            </div>
                          );
                        })()}
                      </div>
                      <div style={{ position: "absolute", bottom: -5, left: "50%", transform: "translateX(-50%)", width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: "5px solid rgba(15, 23, 42, 0.98)" }} />
                    </div>
                  )}

                  <div style={{ width: "100%", display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 3, height: CHART_H }}>
                    <div
                      onMouseEnter={() => { setHov(i); setHovBar("collected"); }}
                      onMouseLeave={() => { setHov(null); setHovBar(null); }}
                      style={{
                        width: 14,
                        flexShrink: 0,
                        height: mounted ? h0 : 0,
                        minHeight: 2,
                        background: isHov && hovBar === "collected"
                          ? "linear-gradient(180deg,#3730a3 0%,#4338ca 100%)"
                          : "linear-gradient(180deg,#6366f1 0%,#4f46e5 100%)",
                        borderRadius: "4px 4px 0 0",
                        transition: mounted ? "height 0.6s cubic-bezier(0.34,1.3,0.64,1), background 0.15s" : "none",
                        transitionDelay: mounted ? `${i * 0.07}s` : "0s",
                        cursor: "pointer",
                        alignSelf: "flex-end",
                        boxShadow: isHov && hovBar === "collected" ? "0 0 0 2px rgba(99,102,241,0.35)" : "none",
                      }}
                    />
                    <div
                      onMouseEnter={() => { setHov(i); setHovBar("target"); }}
                      onMouseLeave={() => { setHov(null); setHovBar(null); }}
                      style={{
                        width: 14,
                        flexShrink: 0,
                        height: mounted ? h1 : 0,
                        minHeight: 2,
                        background: isHov && hovBar === "target" ? "#94a3b8" : "#cbd5e1",
                        borderRadius: "4px 4px 0 0",
                        alignSelf: "flex-end",
                        cursor: "pointer",
                        transition: mounted ? "height 0.6s cubic-bezier(0.34,1.3,0.64,1), background 0.15s" : "none",
                        transitionDelay: mounted ? `${i * 0.07 + 0.03}s` : "0s",
                        boxShadow: isHov && hovBar === "target" ? "0 0 0 2px rgba(148,163,184,0.45)" : "none",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ height: 1, background: "var(--color-border)", opacity: 0.8, marginBottom: 6 }} />

          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
            {labels.map((l, i) => (
              <div key={i} style={{ flex: 1, textAlign: "center", fontSize: 10, color: hov === i ? (hovBar === "target" ? "#475569" : "#6366f1") : "#64748b", fontWeight: hov === i ? 700 : 500, transition: "color .15s" }} className={hov === i ? "" : "text-slate-600 dark:text-slate-400"}>
                {l}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 16, marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--color-border)" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10, color: "#475569" }} className="text-slate-600 dark:text-slate-400 font-medium">
          <span style={{ width: 10, height: 10, borderRadius: 3, background: "linear-gradient(135deg,#6366f1,#4f46e5)", display: "inline-block" }} />
          Collected
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10, color: "#475569" }} className="text-slate-600 dark:text-slate-400 font-medium">
          <span style={{ width: 10, height: 10, borderRadius: 3, background: "#cbd5e1", display: "inline-block" }} />
          Target
        </span>
      </div>
    </div>
  );
}
